import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, ArrowRight, ShieldCheck, Users, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api, { apiErr } from "@/lib/api";
import { ForgotPassword } from "@/components/ForgotPassword";
import { toast } from "sonner";

const HOME = { tenant_admin: "/app", operations: "/app", finance: "/app", reporting: "/app", viewer: "/app", tenant_manager: "/app", publisher: "/publisher" };

export default function TenantLogin({ portal = "staff" }) {
  const { slug } = useParams();
  const { login } = useAuth();
  const nav = useNavigate();
  const [brand, setBrand] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/tenant-brand/${slug}`).then(({ data }) => setBrand(data)).catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email.trim(), password, { tenant_slug: slug, portal });
    setLoading(false);
    if (r.ok) { toast.success(`Welcome, ${r.user.name}`); nav(HOME[r.user.role] || "/"); }
    else toast.error(r.error);
  };

  if (notFound) return <div className="flex min-h-screen items-center justify-center bg-[var(--tf-bg)] p-6 text-center text-slate-400">Workspace login link not found. Please check the URL.</div>;
  const color = brand?.primary_color || "#10b981";
  const isPub = portal === "publisher";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--tf-bg)] p-6">
      <div className="pointer-events-none absolute left-0 top-0 h-80 w-80 rounded-full blur-3xl" style={{ background: `${color}18` }} />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center text-center">
          {brand?.logo ? <img src={brand.logo} alt="" className="mb-3 h-11" /> : <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: color }}><Target className="text-white" size={22} /></div>}
          <h1 className="font-head text-2xl font-bold text-slate-50">{brand?.brand_name || "Loading…"}</h1>
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs font-medium text-slate-300">
            {isPub ? <Users size={12} /> : <Building2 size={12} />} {isPub ? "Publisher Portal" : "Team Portal"}
          </span>
        </div>
        <div className="tf-card p-7">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Email</label>
              <input data-testid="login-email" className="tf-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Password</label>
              <input data-testid="login-password" className="tf-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg py-2.5 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60" style={{ background: color }}>
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>
          <ForgotPassword tenantSlug={slug} accentStyle={{ background: color }} />
        </div>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500"><ShieldCheck size={13} /> Powered by TrackingPanel</p>
      </motion.div>
    </div>
  );
}
